import { useCallback, useRef, useState } from "react";
import { Loader2, Minus, Plus, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function PermList({ rows, kind }) {
  const gain = kind === "gained";
  const Icon = gain ? Plus : Minus;
  return (
    <div className={`rounded-md border p-2 ${gain ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`} data-testid={`perm-preview-${kind}`}>
      <div className={`mb-1 text-xs font-semibold ${gain ? "text-emerald-800" : "text-red-800"}`}>
        {gain ? "Will gain" : "Will lose"} ({rows.length})</div>
      {rows.length ? (
        <ul className="max-h-48 space-y-0.5 overflow-auto text-xs">
          {rows.map((r) => (
            <li key={r.perm} className={`flex items-center gap-1.5 ${gain ? "text-emerald-700" : "text-red-700"}`} data-testid={`perm-preview-${kind}-${r.perm}`}>
              <Icon className="mt-0.5 h-3 w-3 shrink-0 self-start" />
              <span className="min-w-0 break-words">{r.label}<code className="block text-[10px] opacity-60">{r.perm}</code></span></li>
          ))}
        </ul>
      ) : <p className="text-xs text-slate-400">Nothing</p>}
    </div>
  );
}

/** `confirm(url, body, title)` resolves true when the user confirms the previewed change. */
export function usePermissionPreview() {
  const [st, setSt] = useState(null);
  const resolver = useRef(null);
  const close = (ok) => { resolver.current?.(ok); resolver.current = null; setSt(null); };

  const confirm = useCallback((url, body, title) => new Promise((resolve) => {
    resolver.current = resolve;
    const isRole = url.startsWith("/roles/");
    setSt({ title, isRole, loading: true });
    api.post(url, body).then(({ data }) => {
      if (!data.gained.length && !data.lost.length) { resolver.current = null; setSt(null); resolve(true); return; }
      setSt({ title, isRole, data });
    }).catch((e) => setSt({ title, isRole, error: formatApiErrorDetail(e.response?.data?.detail) }));
  }), []);

  const d = st?.data;
  const dialog = (
    <Dialog open={!!st} onOpenChange={(o) => !o && close(false)}>
      <DialogContent className="max-w-2xl" data-testid="perm-preview-dialog">
        <DialogHeader>
          <DialogTitle>{st?.title || "Review permission change"}</DialogTitle>
          <DialogDescription>Check exactly what changes before saving.</DialogDescription>
        </DialogHeader>
        {st?.loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        {st?.error && <p className="text-sm text-red-600" data-testid="perm-preview-error">Could not preview: {st.error}</p>}
        {d && (
          <div className="space-y-3">
            <p className="flex items-center gap-1.5 text-sm text-slate-700" data-testid="perm-preview-affected">
              <Users className="h-4 w-4 text-slate-400" />
              {st.isRole
                ? <><b>{d.users_affected}</b>&nbsp;of {d.total_users} user{d.total_users === 1 ? "" : "s"} on this role affected</>
                : <>This person is affected</>}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <PermList rows={d.gained} kind="gained" />
              <PermList rows={d.lost} kind="lost" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="perm-preview-cancel">Cancel</Button>
          <Button onClick={() => close(true)} disabled={st?.loading} data-testid="perm-preview-confirm">
            {st?.error ? "Save anyway" : "Confirm & save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { confirm, dialog };
}
